import {
  get,
  find,
  isNil,
  join,
  compact,
  filter,
  cloneDeep,
  findIndex,
  snakeCase,
} from 'lodash'
import {
  MARRIED_GROUP,
  SPOUSE_ON_CONTRACT_NA,
  SPOUSE_ON_CONTRACT_NOT_PRESENT,
  SPOUSE_ON_CONTRACT_PRESENT,
  SOMEONE_ELSE,
  SINGLE_GROUP,
} from '../constants'

export const isMarriedBuyer = buyer =>
  MARRIED_GROUP.includes(buyer.marital_status)

export const checkTwoMarriedPeople = (buyers = []) => {
  return filter(buyers, isMarriedBuyer).length >= 2
}

export const getBuyerFullName = buyerInfo =>
  join(
    compact([buyerInfo.first_name, buyerInfo.middle_name, buyerInfo.last_name]),
    ' '
  )

export const getBuyerId = buyerInfo =>
  snakeCase(`${getBuyerFullName(buyerInfo)} ${buyerInfo.email_address}`)

export const findSpouse = (buyers, name, email) =>
  find(buyers, { spouse_name: name, spouse_email_address: email })

export const findSpouseIndex = (buyers, name, email) =>
  findIndex(buyers, { spouse_name: name, spouse_email_address: email })

export const findSpouseById = (buyers, spouseId) =>
  find(buyers, buyer => {
    const buyerId = getBuyerId(get(buyer, 'buyer_party', {}))
    return buyerId === spouseId
  })

export const getSpouseOnContractStatus = buyer => {
  let spouseOnContractStatus = get(buyer, 'spouse_on_contract_status')
  if (!isNil(spouseOnContractStatus)) {
    return spouseOnContractStatus
  }

  const isSpouseOnContract = get(buyer, 'is_spouse_on_contract')
  if (isNil(isSpouseOnContract)) {
    spouseOnContractStatus = SPOUSE_ON_CONTRACT_NA
  } else {
    spouseOnContractStatus = isSpouseOnContract
      ? SPOUSE_ON_CONTRACT_PRESENT
      : SPOUSE_ON_CONTRACT_NOT_PRESENT
  }
  return spouseOnContractStatus
}

export const matchRelationship = (buyers, relationships = {}) =>
  buyers.map(buyer => {
    const res = cloneDeep(buyer)
    const buyerInfo = get(res, 'buyer_party', {})
    const buyerId = getBuyerId(buyerInfo)
    const spouseId = relationships[buyerId]
    const spouse = findSpouseById(buyers, spouseId)
    // here the buyer answered the modal and have an spouse on contract
    /* the reason for adding spouse at the end is:
     * we can have a relationship A married to B,
     * someday B changed his name to B1
     * then A's spouse will be undefined
     * we set both A and B1's spouse info as empty
     */
    if (spouseId && spouseId !== SOMEONE_ELSE && spouse) {
      const spouseInfo = get(spouse, 'buyer_party', {})
      res.is_spouse_on_contract = true
      res.spouse_name = getBuyerFullName(spouseInfo)
      res.spouse_email_address = spouseInfo.email_address
      res.spouse_marital_status = spouse.marital_status
    } else if (spouseId === SOMEONE_ELSE) {
      // here the buyer answered the modal and selected someone else
      res.is_spouse_on_contract = false
      res.spouse_name = null
      res.spouse_email_address = null
      res.spouse_marital_status = null
    } else {
      // here we don't have an answer on the modal yet
      res.is_spouse_on_contract = null
      res.spouse_name = null
      res.spouse_email_address = null
      res.spouse_marital_status = null
    }

    // this is a ui value only, should not get to api
    delete res.spouse_on_contract_status
    return res
  })

export const getRelationship = (spouse, buyer) => {
  // Assume buyer haven't answered relationships modal
  let res = ''

  if (spouse) {
    // if spouse is present
    const spouseId = getBuyerId(get(spouse, 'buyer_party', {}))
    res = spouseId
  } else {
    const spouseOnContractStatus = getSpouseOnContractStatus(buyer)
    if (spouseOnContractStatus !== SPOUSE_ON_CONTRACT_NA) {
      // If buyer doesn't have spouse in contract
      res = SOMEONE_ELSE
    }
  }
  return res
}

export const buildRelationshipsMap = marriedBuyers => {
  const relationships = {}
  marriedBuyers.forEach(currentBuyer => {
    const currentBuyerInfo = get(currentBuyer, 'buyer_party', {})
    const currentBuyerId = getBuyerId(currentBuyerInfo)
    const currentBuyerName = getBuyerFullName(currentBuyerInfo)
    const currentBuyerEmail = get(currentBuyerInfo, 'email_address', '')

    const spouse = findSpouse(
      marriedBuyers,
      currentBuyerName,
      currentBuyerEmail
    )

    relationships[currentBuyerId] = getRelationship(spouse, currentBuyer)
  })
  return relationships
}

export const generateRelationshipModalProps = buyers => {
  const initialRelationships = {}
  const marriedBuyers = filter(buyers, isMarriedBuyer)
  const relationshipNames = marriedBuyers.map((buyer, i) => {
    const buyerInfo = get(buyer, 'buyer_party', {})
    const buyerFullName = getBuyerFullName(buyerInfo)
    const buyerId = getBuyerId(buyerInfo)
    const spouse = findSpouse(
      marriedBuyers,
      buyerFullName,
      buyerInfo.email_address
    )
    initialRelationships[buyerId] = getRelationship(spouse, buyer)
    return {
      buyerId,
      name: buyerFullName,
      isDisplayedField: true,
    }
  })
  relationshipNames.push({
    buyerId: SOMEONE_ELSE,
    name: SOMEONE_ELSE,
    isDisplayedField: false,
  })

  return { relationshipNames, initialRelationships }
}

export const updateMaritalStatus = buyers => {
  const resultBuyers = cloneDeep(buyers)
  resultBuyers.forEach(buyer => {
    const buyerInfo = get(buyer, 'buyer_party', {})
    /**
     * If buyer isn't married to anyone ensure spouse fields
     * are empty and clear spouse info if any
     */
    if (SINGLE_GROUP.includes(buyer.marital_status)) {
      // find buyer spouse index if any
      const spouseIndex = findSpouseIndex(
        buyers,
        getBuyerFullName(buyerInfo),
        buyerInfo.email_address
      )

      // if there's a spouse clear their relationship
      if (spouseIndex !== -1) {
        const spouseBuyer = buyers[spouseIndex]
        spouseBuyer.is_spouse_on_contract = null
        spouseBuyer.spouse_name = null
        spouseBuyer.spouse_email_address = null
        spouseBuyer.spouse_marital_status = null
        resultBuyers[spouseIndex] = spouseBuyer
      }

      // update main buyer in the same index
      buyer.is_spouse_on_contract = null
      buyer.spouse_name = null
      buyer.spouse_email_address = null
      buyer.spouse_marital_status = null
    }
  })

  return resultBuyers
}
